// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { findIndex } from 'lodash';

import ProgressiveRenderList from 'app/components/commons/ProgressiveRenderList';
import t from 'app/utils/translate';
import OneScoreWidget from '../../widgets/one-sport/OneScoreWidget';
import OpenEntityRow from './OpenEntityRow';
import WidgetEntityRow from './WidgetEntityRow';
import { getFactoredEntityPositionSalary } from '../../selectors/salaryExpenditureSelector';

type Props = {
  darkMode: boolean,
  disabled?: boolean,
  displayWidget?: boolean,
  innerRef?: () => void,
  isAnimating?: boolean,
  isRacing?: boolean,
  onAdd: Object => void,
  onRemove: number => void,
  onWidgetClick: string => void,
  players: Array<Object>,
  playerSelectionTableInfo: Object,
  positionFilter?: string | null,
  salaryCap?: number,
  salaryExpenditure: number,
  showPlayerSelections: boolean,
  widgetPlayerId?: string,
  setRef: (id: string, ref: any) => void
};

const Table = styled.div({
  position: 'relative'
});

const StyledProgressiveRenderList = styled(ProgressiveRenderList)({
  outline: 'none'
});

const NoResults = styled.div(({ darkMode, theme }) => ({
  backgroundColor: darkMode ? theme.color.basis1 : theme.color.invertableBasis1,
  padding: '1.5rem 1rem',
  textAlign: 'center'
}));

// NOTE - we get a performance win here by using PureComponent
// using a pure functional component will re-render this PlayerSelectionTable
// even when you change tabs in the ChallengeHead. It's very expensive to re-render
// the PlayerSelectionTable!
//
class PlayerSelectionTable extends React.PureComponent<Props> {
  props: Props;

  static defaultProps = {
    disabled: false,
    displayWidget: false,
    innerRef: undefined,
    isAnimating: false,
    isRacing: false,
    salaryCap: undefined,
    positionFilter: null,
    widgetPlayerId: undefined
  };

  componentDidUpdate(prevProps: Props) {
    const { widgetPlayerId, players } = this.props;
    const length = this.progressiveRenderList.getNumRendered();

    if (length === prevProps.players.length) {
      return;
    }

    if (findIndex(players, { idStr: widgetPlayerId }) >= length) {
      // to ensure we can find a player and scroll to it we must first render the whole list
      this.progressiveRenderList.loadAll();
    }
  }

  progressiveRenderList;

  renderEntity = (player: Object) => {
    const {
      disabled,
      displayWidget,
      isAnimating,
      isRacing,
      onAdd,
      onRemove,
      onWidgetClick,
      playerSelectionTableInfo,
      positionFilter,
      salaryCap,
      salaryExpenditure,
      showPlayerSelections,
      widgetPlayerId,
      setRef
    } = this.props;

    const {
      average,
      form,
      popularity,
      salary,
      positions,
      weight,
      winProjected,
      placeProjected,
      dualAccepted
    } = player;

    const factoredSalary = getFactoredEntityPositionSalary(salary, positions, positionFilter);
    return (
      <div key={player.idStr}>
        <OpenEntityRow
          awayTeam={player.awayAbbr}
          barrier={player.barrier}
          canAfford={factoredSalary <= salaryCap - salaryExpenditure}
          disabled={(disabled && !player.selected) || player.disabled || dualAccepted}
          positionFilter={positionFilter}
          gameOrder={player.gameOrder}
          homeTeam={player.homeAbbr}
          id={player.id}
          idStr={player.idStr}
          image={player.imageURI}
          initials={player.initials}
          isAnimating={isAnimating}
          isHome={player.isHome}
          isRacing={isRacing}
          isScratched={player.isScratched}
          isTeam={player.isTeam}
          labels={player.labels}
          name={player.name}
          number={player.number}
          onClick={() => {
            onWidgetClick(player.idStr);
          }}
          playerSelectionTableInfo={playerSelectionTableInfo}
          positionStr={player.positionStr}
          positions={player.positions}
          reverseOrder={player.reverseOrder}
          selected={player.selected}
          selectedPosition={player.selectedPosition}
          showPlayerSelections={showPlayerSelections}
          showWidget={displayWidget && player.idStr === widgetPlayerId}
          teamName={player.teamName}
          ref={ref => {
            setRef(player.id, ref);
          }}
          rider={player.rider}
          riderImage={player.riderImage}
          onSelect={onAdd}
          onRemove={onRemove}
          tableData={{
            average,
            form,
            salary: factoredSalary,
            weight,
            winProjected,
            placeProjected,
            popularity
          }}
          trainer={player.trainer}
        />
        {displayWidget && (
          <OneScoreWidget
            id={player.id}
            isOpen={player.idStr === widgetPlayerId}
            isRacing={isRacing}
            isTeam={player.isTeam}
            onClose={() => {
              onWidgetClick(player.idStr);
            }}
            renderEntity={() => (
              <WidgetEntityRow
                id={player.id}
                idStr={player.idStr}
                disabled={(disabled && !player.selected) || player.disabled}
                isRacing={isRacing}
                isTeam={player.isTeam}
                selected={player.selected}
                onSelect={(...args) => {
                  onAdd(...args);
                  onWidgetClick(player.idStr);
                }}
                onRemove={(...args) => {
                  onRemove(...args);
                  onWidgetClick(player.idStr);
                }}
                selectedPosition={player.selectedPosition}
              />
            )}
          />
        )}
      </div>
    );
  };

  renderItem = (index: number) => {
    const { darkMode, players = [] } = this.props;
    const player = players[index];

    if (!player) {
      return (
        <NoResults key="no-results" darkMode={darkMode}>
          {t('No results found')}
        </NoResults>
      );
    }

    return this.renderEntity(player);
  };

  render() {
    const { innerRef, players, widgetPlayerId } = this.props;
    return (
      <Table ref={innerRef}>
        <StyledProgressiveRenderList
          ref={ref => {
            this.progressiveRenderList = ref;
          }}
          initialAmount={20}
          itemRenderer={this.renderItem}
          length={players.length || 1}
          players={players} // force PureComponent update
          widgetPlayerId={widgetPlayerId} // force PureComponent update
        />
      </Table>
    );
  }
}

function mapStateToProps(state) {
  return {
    darkMode: state.settings.darkMode
  };
}

export default connect(mapStateToProps)(PlayerSelectionTable);
